import { AUTH_QUERY_VALUES, AUTH_SESSION_KEYS } from "@/constants/authFlowKeys";
import { deleteCookie as deleteLoginCookie } from "@utils/login";
import { isLocalAuthHost } from "@utils/login/loginHelpers";
import { getAuthProvider } from "./providers";
import { notifyCurrentUserChanged } from "./currentUser";
import { deleteCookie as deleteSharedCookie } from "../../screens/utils/cookie";
import { clearFederationRedirectState } from "@services/auth/providers/forgerock/federationRedirectState";
import {
  removeAuthInfo,
  removeOidcStorageEntries,
  resetAccessTokenCache,
} from "./tokenService";

// Tears down partial OIDC state left behind by an interrupted callback,
// failed hydration, or cross-tab logout detection.
export const clearIncompleteAuthSession = async ({ userManager } = {}) => {
  sessionStorage.removeItem(AUTH_SESSION_KEYS.POST_CALLBACK_AUTH_PENDING);
  // Legacy key from the pre-machine grace-period system; cleared defensively.
  sessionStorage.removeItem(
    AUTH_SESSION_KEYS.POST_CALLBACK_AUTH_GRACE_STARTED_AT,
  );
  clearFederationRedirectState();
  removeOidcStorageEntries();
  removeAuthInfo();

  try {
    await userManager?.removeUser?.();
  } catch (error) {
    console.error("Unable to clear incomplete auth session.", error);
  }
};

// Removes shared-domain and optional localhost federation cookies.
// Used during manual logout. Auto-logout uses clearStaleSessionCookie instead
// to preserve federation type for auto-redirect on re-login.
export const clearFederationCookies = ({
  includeSharedDomain = false,
  includeLocalHostCopies = false,
} = {}) => {
  if (includeSharedDomain) {
    deleteSharedCookie("engageCorpUser", true);
    deleteSharedCookie("iPlanetDirectoryPro", true);
    deleteSharedCookie("askEngage", true);
  }

  if (includeLocalHostCopies) {
    deleteLoginCookie("engageCorpUser");
    deleteLoginCookie("iPlanetDirectoryPro");
  }
};

// Clears only the ForgeRock session cookie without touching federation cookies
// (engageCorpUser, askEngage). Used during auto-logout to break the redirect
// loop caused by a stale iPlanetDirectoryPro cookie while preserving the
// federation type so federated users can still auto-redirect on re-login.
export const clearStaleSessionCookie = ({
  isLocalHost = isLocalAuthHost(),
} = {}) => {
  deleteSharedCookie("iPlanetDirectoryPro", true);

  if (isLocalHost) {
    deleteLoginCookie("iPlanetDirectoryPro");
  }
};

// Wipes localStorage, sessionStorage, and the in-memory access token cache.
// Preserves desiredNav (pre-login URL) and the federated realm hint, and seeds
// the local-auth skip flag on ForgeRock localhost.
export const resetLogoutBrowserState = ({
  desiredNav,
  isLocalHost = isLocalAuthHost(),
} = {}) => {
  // Every storage access below is best-effort, the same way the Entra domainHint module
  // treats it. Storage is not guaranteed to be there: Safari private mode, a
  // blocked or partitioned third-party context, and an exhausted quota all make
  // getItem/clear/setItem throw rather than return null. This runs on the
  // logout path, so a throw here used to abandon the rest of the teardown --
  // including the token cache reset below.
  //
  // Read before the wipe, restored after. The federated realm belongs to the
  // workstation rather than the session, so a GST/SET user who signs out should
  // land back on their own login page instead of Microsoft's generic one — and
  // unlike the initial visit there is no ?domain_hint= left to re-read by then.
  // See providers/entra/domainHint, which owns the read side and re-validates the value.
  let domainHint = null;
  let forceAccountSelection = null;
  try {
    domainHint = localStorage.getItem(AUTH_SESSION_KEYS.DOMAIN_HINT);
    // Another tab's manual logout may have just armed the account picker; a
    // session-expiry wipe here must not disarm it (see providers/entra/accountSelection).
    forceAccountSelection = localStorage.getItem(
      AUTH_SESSION_KEYS.FORCE_ACCOUNT_SELECTION,
    );
  } catch {
    // Storage unavailable — nothing to carry across the wipe.
  }

  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    // Storage unavailable — nothing persisted, so nothing to wipe.
  }
  notifyCurrentUserChanged();

  // Deliberately outside every try block: this is in-memory, cannot throw, and
  // is the one step that must never be skipped. A surviving access token would
  // keep authorizing API calls after the user believes they have signed out.
  resetAccessTokenCache();

  try {
    if (domainHint) {
      localStorage.setItem(AUTH_SESSION_KEYS.DOMAIN_HINT, domainHint);
    }
    if (forceAccountSelection) {
      localStorage.setItem(
        AUTH_SESSION_KEYS.FORCE_ACCOUNT_SELECTION,
        forceAccountSelection,
      );
    }

    // The flag's sole reader is useLoginInitialization, inside the Engage
    // /login page, so only a provider with that page needs it.
    if (isLocalHost && getAuthProvider().hasInAppLoginPage) {
      sessionStorage.setItem(
        AUTH_SESSION_KEYS.SKIP_AUTO_SESSION_CHECK_ONCE,
        AUTH_QUERY_VALUES.TRUE,
      );
    }

    if (desiredNav) {
      sessionStorage.setItem(AUTH_SESSION_KEYS.DESIRED_NAV, desiredNav);
    }
  } catch {
    // Storage unavailable — these three are conveniences (realm routing, the
    // ForgeRock skip flag, restoring the pre-login URL), not correctness. The
    // sign-out itself has already happened.
  }
};
